import React, { FC, useMemo } from 'react';

import classNames from 'classnames';

import { EvSection, TABLE_SR_SUMMARY_IDS } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { ErrorView } from 'src/apps/dashboard/components/error-view/ErrorView';
import { BUSINESS_UNIT_WFA_TABLE_ID } from 'src/apps/dashboard/constants/Dashboard';
import { useWfaSessionsQuery } from 'src/apps/dashboard/hooks/useWfaSessionsQuery';
import { getWfaSessionColumns } from 'src/apps/dashboard/pages/business-units/helpers/WfaSessionColumns';
import { ERROR_TYPE } from 'src/apps/dashboard/types/chartErrors';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import './BusinessUnitWfaTable.scss';

interface IBusinessUnitWfaTableProps {
	dashboardFilters: IFilterOptions;
	unitIdLabel?: string;
}

export const BusinessUnitWfaTable: FC<IBusinessUnitWfaTableProps> = ({
	dashboardFilters,
	unitIdLabel
}) => {
	const { getTableStateById } = useTableState();
	const tableState = getTableStateById(BUSINESS_UNIT_WFA_TABLE_ID);
	const { tableSort, paginationInfo } = tableState;

	const testPeriod = dashboardFilters?.dateRange
		? String(dashboardFilters.dateRange[0].value)
		: '30';
	const customFieldFilters: IFilterOptions = omit(dashboardFilters ?? {}, ['dateRange']);
	customFieldFilters.unitId = [{ label: unitIdLabel, value: unitIdLabel }];

	const [sessionsQuery] = useWfaSessionsQuery({
		filters: customFieldFilters,
		dateRange: testPeriod,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage
	});

	const total = sessionsQuery.data?.total ?? 0;
	const unfilteredTotal = sessionsQuery.data?.unfilteredTotal ?? 0;

	const rows = sessionsQuery.data?.rows ?? [];

	const isTableLoading =
		sessionsQuery.isLoading ||
		sessionsQuery.isPreviousData ||
		(sessionsQuery.isStale && sessionsQuery.isRefetching);

	const tableOptions = useMemo(
		() => ({
			keyField: 'sessionId',
			title: 'Web Flow sessions',
			caption: 'Web Flow table',
			pagination: true,
			dataType: 'sessions',
			remote: true
		}),
		[]
	);

	if (isTableLoading) {
		return (
			<EvSection
				ariaLabel="Web Flow Analyzer Tests Table"
				ariaLive="polite"
				ariaDescribedby={TABLE_SR_SUMMARY_IDS}
				skipLinkId="main-section"
				className={classNames('dashboard-section', 'table-section')}
			>
				<TableSkeleton columns={6} rowCount={5} />
			</EvSection>
		);
	}

	if (sessionsQuery.isError || (rows.length === 0 && !isTableLoading)) {
		const errorType = sessionsQuery.isError ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA;

		return <ErrorView errorType={errorType} />;
	}

	return (
		<EvSection
			ariaLabel="Web Flow Analyzer Tests Table"
			ariaLive="polite"
			ariaDescribedby={TABLE_SR_SUMMARY_IDS}
			skipLinkId="main-section"
			className={classNames('dashboard-section', 'table-section')}
		>
			<PlatformTable
				isTableLoading={isTableLoading}
				tableId={BUSINESS_UNIT_WFA_TABLE_ID}
				tableData={rows}
				totalPaginationableResults={total}
				totalCount={unfilteredTotal}
				className="business-unit-wfa-table"
				persistTableState
				columns={getWfaSessionColumns()}
				options={tableOptions}
			/>
		</EvSection>
	);
};
